import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Typography,
  Container,
  Paper,
  Box,
  Avatar,
  Tooltip,
  Button,
  Divider,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { getPublishedArticles } from '../queries/articles';
import { getTermsByType } from '../queries/termsAndConditions';
import { formatDate as formatDisplayDate } from '../utils/helper';

const aboutContent = (
  <>
    <Typography variant='body2' sx={{ mb: 0.5, fontSize: '0.85rem' }}>
      👋{' '}
      <a
        href='#industry-professionals'
        style={{
          textDecoration: 'underline',
          color: 'rgba(0, 0, 0, 0.6)',
        }}
        onMouseOver={(e) => (e.currentTarget.style.color = '#1976d2')}
        onMouseOut={(e) => (e.currentTarget.style.color = 'rgba(0, 0, 0, 0.6)')}
      >
        Are you an industry professional? Use your network. Get paid. Simple. →
      </a>
    </Typography>

    <Divider sx={{ my: 2, borderColor: '#e0e0e0' }} />

    <Typography variant='h6' fontWeight='bold' gutterBottom>
      We've been on the front lines.
    </Typography>
    <Typography paragraph>
      Built go-to-market strategies. Opened up Europe for US tech companies and vice versa. And we've seen the same
      pattern, again and again: great founders, great products — tiny pipeline.
    </Typography>

    <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 4 }}>
      Trust us — we've been there.
    </Typography>
    <Typography paragraph>
      Built it. Launched it. Waited. Hoped. And nothing happened. Not because the product wasn't good. But because not
      enough people saw it.
    </Typography>

    <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 4 }}>
      Great products don't sell themselves anymore.
    </Typography>
    <Typography paragraph>
      There's too much noise. Decision-makers today are overwhelmed. Everyone is "disrupting" something.
    </Typography>

    <Typography paragraph>Meanwhile, companies are still saying things like:</Typography>
    <ul style={{ paddingLeft: '1.2rem' }}>
      <li>"Let's test the market."</li>
      <li>"Let's hire a local rep."</li>
      <li>"We'll figure it out."</li>
    </ul>
    <Typography paragraph>
      What happens next? You hire someone who talks a big game. You burn 6–12 months waiting for momentum. You realize
      you've made no real progress — and now you're stuck.
    </Typography>

    <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 4 }}>
      Cold email works — until it doesn't.
    </Typography>
    <Typography paragraph>
      We ran email campaigns for top products. Clear value, risk-free, even with guarantees. Still hard.
    </Typography>
    <ul style={{ paddingLeft: '1.2rem' }}>
      <li>Many are doing it</li>
      <li>Decision-makers are drowning in email</li>
      <li>A "no" in email is a dead end — you won't get another shot</li>
    </ul>
    <Typography paragraph>Cold outreach has its place. But it won't get you there on its own.</Typography>

    <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 4 }}>
      Marketing helps — but not when you need quick and cheap results.
    </Typography>
    <Typography paragraph>It builds awareness. It builds trust. It brings in leads — eventually maybe.</Typography>
    <ul style={{ paddingLeft: '1.2rem' }}>
      <li>You're spending before you're learning</li>
      <li>You need to localize — copy, design, channels, everything</li>
      <li>You're hoping inbound will land in front of the right people</li>
    </ul>
    <Typography paragraph>
      It's a long game. A solid marketing strategy is key. But it won't crack open a market this quarter.
    </Typography>

    <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 4 }}>
      Events can be powerful — but they don't scale.
    </Typography>
    <Typography paragraph>
      Standing on a stage gives you visibility. You shake hands, meet some names, get some follow-ups.
    </Typography>
    <ul style={{ paddingLeft: '1.2rem' }}>
      <li>You fly in, prep, spend, and follow up for weeks</li>
      <li>It's one country, maybe even only once a year — and it doesn't scale</li>
      <li>The biggest event in some local market might not be in English - so how are you going to present?</li>
    </ul>

    <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 4 }}>
      Hiring locally looks smart — until it's not.
    </Typography>
    <Typography paragraph>
      The classic move. Hire a country manager. They say they have a strong network. Maybe they do.
    </Typography>
    <ul style={{ paddingLeft: '1.2rem' }}>
      <li>You won't know for 6–12 months</li>
      <li>You've burned budget, time, and momentum</li>
      <li>You're still not sure if there's a product-market fit</li>
      <li>You're left with overhead, weak results, and a name that's taken a hit.</li>
    </ul>

    <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 4 }}>
      What about channel partners or resellers?
    </Typography>
    <ul style={{ paddingLeft: '1.2rem' }}>
      <li>They often carry more than just your products</li>
      <li>They rarely push yours unless there's money up front</li>
      <li>You're not their priority — you're a line item</li>
      <Typography paragraph>Resellers can work and are worth the try, but they're hard to manage.</Typography>
    </ul>

    <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 4 }}>
      The bottom line?
    </Typography>
    <Typography paragraph>
      All of these tactics matter in some shape or form. And to a certain extent they belong in a proper go-to-market
      plan. But none of them gives you fast access to real buyers in unfamiliar markets.
    </Typography>

    <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 4 }}>
      So how do you break through? That's where warm introductions come in.
    </Typography>
    <Typography paragraph>From industry professionals who already know your buyers. People who:</Typography>
    <ul style={{ paddingLeft: '1.2rem' }}>
      <li>Work in your target industry</li>
      <li>Are based in the local market</li>
      <li>Know how buying decisions are made and who makes them</li>
    </ul>

    <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 4 }}>
      Why it works:
    </Typography>
    <ul style={{ paddingLeft: '1.2rem' }}>
      <li>It's faster</li>
      <li>It's lean</li>
      <li>It scales</li>
      <li>It's low risk</li>
      <li>You land real clients before hiring locally</li>
      <li>Allows you to test multiple markets at once</li>
    </ul>

    <Typography paragraph>
      If you land clients this way, you know there's demand. That's when you hire — with confidence, with proof, and
      with much better odds of success.
    </Typography>

    <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 4 }}>
      But here's the real problem.
    </Typography>
    <Typography paragraph>
      Managing this kind of outreach is messy. We tried it with spreadsheets, Notion docs, and custom hacks. It doesn't
      scale.
    </Typography>

    <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 4 }}>
      Industry professionals won't move on vague promises.
    </Typography>
    <Typography paragraph>
      If you want someone to back your product and make real introductions, you need to:
    </Typography>
    <ul style={{ paddingLeft: '1.2rem' }}>
      <li>Pitch them right — they need to believe in your product.</li>
      <li>Reward them in the right way</li>
      <li>Handle admin, legal, and payments without friction</li>
    </ul>

    <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 4 }}>
      That's why we built TechXpander.
    </Typography>
    <Typography paragraph>Not as a side project. But as a platform to fix what broke for us.</Typography>
    <Typography paragraph>
      TechXpander helps you connect with trusted professionals, run referral-based outreach, and manage it all — without
      spinning up offices or hiring full-time teams too early.
    </Typography>

    {/* Tech Company Section */}
    {/* ... Existing tech company content remains unchanged ... */}

    <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 4 }}>
      This isn't guesswork.
    </Typography>
    <Typography paragraph>It's the platform we wish we had 5 years ago. Now it's yours.</Typography>

    {/* Register as a Client Button */}
    <Box mt={2}>
      <Tooltip
        title='Sign up to share your product and commission plan. We may connect you with industry insiders who know your buyers, and you can also invite your own.'
        arrow
      >
        <Button variant='contained' color='primary' component={Link} to='/register'>
          Register as a Client
        </Button>
      </Tooltip>
    </Box>

    {/* Lead Agent Section - visually separated */}
    <Box
      id='industry-professionals'
      sx={{ mt: 8, px: 3, py: 5, bgcolor: '#f7f7f7', borderRadius: 2, borderTop: '1px solid #ddd' }}
    >
      <Typography variant='h5' fontWeight='bold' gutterBottom>
        For Industry Professionals
      </Typography>
      <Typography paragraph>
        You know your market. You've built real trust. You understand how decisions get made — and who makes them.
      </Typography>
      <Typography paragraph>But most of that value just sits there.</Typography>

      <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 3 }}>
        Your professional network goes underused
      </Typography>
      <ul style={{ paddingLeft: '1.2rem' }}>
        <li>If you're employed, it serves one company — your employer.</li>
        <li>If you're a freelancer, consultant or a reseller, you use it for the clients you've signed.</li>
        <li>If you're in between roles or even retired, it often sits idle.</li>
      </ul>
      <Typography paragraph>That's a lot of value left untouched.</Typography>
      <Typography paragraph>There's a much bigger opportunity here.</Typography>

      <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 3 }}>
        But who's asking?
      </Typography>
      <Typography paragraph>
        You rarely get approached for referrals by tech companies. And when you do, it's one at a time — messy and
        unclear.
      </Typography>
      <ul style={{ paddingLeft: '1.2rem' }}>
        <li>They send a deck.</li>
        <li>Maybe a doc.</li>
        <li>Then ask you to "help out."</li>
      </ul>

      <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 3 }}>
        If you say yes, it's a hassle
      </Typography>
      <ul style={{ paddingLeft: '1.2rem' }}>
        <li>Each company has its own terms. You're asked to sign their contract.</li>
        <li>You read, redline, go back and forth — just to do maybe four or five intros.</li>
        <li>You need to figure out how commission works. When it kicks in. How to track it. How to invoice.</li>
        <li>You ask yourself: what is a fair commission anyway? How much time do I need to invest?</li>
        <li>Then you're doing admin. Your chasing updates. Hoping it all goes well.</li>
      </ul>
      <Typography paragraph>It's not worth it. So most don't bother, even if they're approached.</Typography>

      <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 3 }}>
        That's why we built TechXpander
      </Typography>
      <ul style={{ paddingLeft: '1.2rem' }}>
        <li>One contract. One clean way of working.</li>
        <li>Multiple vetted clients — ready to go.</li>
        <li>Standardized terms. Automated tracking. Admin handled. One invoice.</li>
        <li>You know exactly what you're signing up for — and why it's worth it.</li>
      </ul>

      <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 3 }}>
        Now it makes sense
      </Typography>
      <ul style={{ paddingLeft: '1.2rem' }}>
        <li>Sign once. Access many.</li>
        <li>Work with the ones that make sense to you.</li>
        <li>You stay in control. You choose who to introduce — and when.</li>
      </ul>
      <Typography paragraph>This model works for different people in different ways:</Typography>
      <ul style={{ paddingLeft: '1.2rem' }}>
        <li>If you're freelance — it keeps you close to the market.</li>
        <li>If you're in between jobs — you stay visible and active.</li>
        <li>If you're a consultant or a reseller — you add more value to what you already do.</li>
        <li>If you're employed and allowed to make intros — this becomes a smart side stream.</li>
        <li>If you're retired — it's a low-effort way to keep your network moving.</li>
      </ul>

      <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 3 }}>
        I wish someone had offered me this years ago.
      </Typography>
      <Typography paragraph>So we built it. For you.</Typography>

      {/* Become a Lead Agent Button */}
      <Box mt={2}>
        <Tooltip title="Sign up and get access within a day to view active programs and check if it's a match." arrow>
          <Button variant='contained' color='primary' component={Link} to='/register'>
            Become a Lead Agent
          </Button>
        </Tooltip>
      </Box>
    </Box>

    {/* Bio Section - at the bottom */}
    <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ mt: 8 }}>
      Who's behind this?
    </Typography>
    <Box display='flex' alignItems='center' mt={2} mb={3}>
      <Avatar alt='Siebren Roorda' src='/path-to-your-photo.jpg' sx={{ width: 64, height: 64, mr: 2 }} />
      <Box>
        <Typography variant='subtitle1' fontWeight='bold'>
          Siebren Roorda
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Founder, TechXpander
        </Typography>
      </Box>
    </Box>
    <Typography variant='body2' color='text.secondary'>
      15 years in international sales and business development for tech companies, mainly in digital marketing and
      advertising. Worked with PubMatic, AppLovin, Membrana Media, AWIN, TBG (now Sprinklr) and others. Built
      go-to-market plans across Europe and North America. Opened doors through trusted industry contacts in key markets.
      Eventually launched TechXpander — to help himself, his clients, and other tech companies grow faster in a world
      where decision-maker attention is fading.
    </Typography>
  </>
);

const contentMap = {
  about: {
    title: 'About Us',
    body: aboutContent,
  },
  contact: {
    title: 'Contact Us',
    body: (
      <Box>
        <Typography paragraph>
          Got a question? Want to talk about how TechXpander can help your business grow? Just reach out — we're happy
          to connect.
        </Typography>
        <Box display='flex' alignItems='center' mb={2}>
          <Avatar
            src='https://flagcdn.com/w40/nl.png'
            alt='Dutch Flag'
            variant='square'
            sx={{ width: 24, height: 16, mr: 1 }}
          />
          <Typography variant='body1'>
            <strong>Mobile:</strong> +31 6 19 598 489
          </Typography>
        </Box>
        <Box display='flex' alignItems='center' mb={1}>
          <Avatar
            alt='Email'
            src='https://upload.wikimedia.org/wikipedia/commons/thumb/4/4e/Mail_%28iOS%29.svg/1024px-Mail_%28iOS%29.svg.png'
            sx={{ width: 20, height: 20, mr: 1 }}
          />
          <Typography variant='body1'>
            <strong>Email:</strong> <a href='mailto:support@techxpander.com'>support@techxpander.com</a>
          </Typography>
        </Box>
      </Box>
    ),
  },

  commissionExamples: {
    title: "Here's What a Lead Agent Can Earn (Example)",
    body: (
      <Box>
        <Typography variant='body2' color='text.secondary' fontStyle='italic' gutterBottom sx={{ mb: 2 }}>
          This is just one example to show how earnings can add up. Real numbers depend on each client, deal size, and
          their commission plan.
        </Typography>

        <Typography variant='h6' fontWeight='bold' gutterBottom>
          Situation:
        </Typography>
        <Typography paragraph>
          Before a lead agent introduces a prospect to a TechXpander (TX) client, the prospect's decision-maker must be
          approved by the client. Once approved — and the introduction is made — TX clients typically follow a simple
          commission structure:
        </Typography>

        <Typography component='ul' sx={{ pl: 3, mb: 2 }}>
          <li>Fixed payouts for key milestones (paid even if no deal closes)</li>
          <li>Monthly commission once the deal is live and generating revenue</li>
        </Typography>

        <Typography paragraph sx={{ mt: 2 }}>
          Example: Let's say a lead agent introduces a prospect who later becomes a customer of the TX client,
          generating $4,000/month in revenue. Here's how earnings could look in that case:
        </Typography>

        <Typography variant='h6' fontWeight='bold' gutterBottom>
          Fixed payouts for key milestones
        </Typography>
        <Typography variant='body2' sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 2 }}>
          (Paid even if no deal closes)
        </Typography>

        <Box
          sx={{
            backgroundColor: '#f9f9f9',
            p: { xs: 1, sm: 2, md: 3 },
            borderRadius: 2,
            boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
            mb: 4,
            overflowX: 'auto',
          }}
        >
          <table style={{ width: '100%', minWidth: '260px', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Payment Type</th>
                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Amount (USD)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>First Meeting</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$600</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>Second Meeting</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$400</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>Go Live Milestone</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$1,500</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                  <strong>Total Initial Payments</strong>
                </td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                  <strong>$2,500</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </Box>

        <Typography variant='h6' fontWeight='bold' gutterBottom>
          Monthly Revenue Commission
        </Typography>
        <Typography variant='body2' sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 2 }}>
          (Applies once the prospect becomes a paying customer and generates $4,000/month for the TX client)
        </Typography>

        <Box
          sx={{
            backgroundColor: '#f9f9f9',
            p: { xs: 1, sm: 2, md: 3 },
            borderRadius: 2,
            boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
            mb: 4,
            overflowX: 'auto',
          }}
        >
          <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ width: '26%', padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Year</th>
                <th style={{ width: '22%', padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>
                  Commission Percentage
                </th>
                <th style={{ width: '26%', padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>
                  Monthly Revenue Commission (USD)
                </th>
                <th style={{ width: '26%', padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>
                  Yearly Revenue Commission (USD)
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>Year 1</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>20%</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$800</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$9,600</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>Year 2</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>15%</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$600</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$7,200</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>Year 3+</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>10%</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$400</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$4,800 per year</td>
              </tr>
            </tbody>
          </table>
        </Box>

        <Typography variant='h6' fontWeight='bold' gutterBottom>
          Earnings Breakdown
        </Typography>

        <Box
          sx={{
            backgroundColor: '#f9f9f9',
            p: { xs: 1, sm: 2, md: 3 },
            borderRadius: 2,
            boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
            mb: 4,
            overflowX: 'auto',
          }}
        >
          <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ width: '28%', padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Year</th>
                <th style={{ width: '22%', padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>
                  Initial Payments
                </th>
                <th style={{ width: '25%', padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>
                  Monthly Revenue Commission (USD)
                </th>
                <th style={{ width: '25%', padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>
                  Total Earnings (USD)
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>Year 1</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$2,500</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$9,600</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$12,100</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>End of Year 3</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$2,500</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$21,600</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$24,100</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>End of Year 5</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$2,500</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$31,200</td>
                <td style={{ padding: '8px', border: '1px solid #ddd' }}>$33,700</td>
              </tr>
            </tbody>
          </table>
        </Box>

        <Typography variant='body2' sx={{ fontStyle: 'italic', color: 'text.secondary', mt: -2 }}>
          This is just one example — actual earnings may vary per client and commission structure.
        </Typography>
        <Typography variant='body2' sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 1 }}>
          Some TX clients offer extra rewards for strong performance — including bonuses for consistent results, small
          retainers, or a mix of both.
        </Typography>
      </Box>
    ),
  },

  'whats-new': {
    title: "What's New",
    body: (
      <Box>
        {/* Top Article List */}
        <Box
          sx={{
            backgroundColor: '#f4f6fa',
            p: 3,
            borderRadius: 2,
            mb: 4,
          }}
        >
          <Typography variant='h6' fontWeight='bold' gutterBottom>
            Recent Updates
          </Typography>
          <ul style={{ listStyle: 'none', paddingLeft: 0, marginBottom: 0 }}>
            <li style={{ marginBottom: '0.6rem' }}>
              <Typography variant='body2' component='span' sx={{ color: '#666', fontSize: '0.8rem', marginRight: 1 }}>
                10 April 2025
              </Typography>
              <a
                href='#opinion-article'
                style={{
                  textDecoration: 'none',
                  color: '#1976d2',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                }}
              >
                In the Age of AI, Trust Still Closes Deals
              </a>
            </li>
            <li>
              <Typography variant='body2' component='span' sx={{ color: '#666', fontSize: '0.8rem', marginRight: 1 }}>
                8 April 2025
              </Typography>
              <a
                href='#launch-article'
                style={{
                  textDecoration: 'none',
                  color: '#1976d2',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                }}
              >
                TechXpander Launches Platform
              </a>
            </li>
          </ul>
        </Box>

        {/* Article 1 - Opinion Piece */}
        <Box
          id='opinion-article'
          sx={{
            backgroundColor: '#ffffff',
            p: { xs: 3, md: 4 },
            borderRadius: 2,
            boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
            mb: 5,
          }}
        >
          <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic', mb: 1 }}>
            10 April 2025
          </Typography>
          <Typography variant='h5' fontWeight='bold' gutterBottom>
            In the Age of AI, Trust Still Closes Deals
          </Typography>

          <Typography paragraph>
            AI is reshaping how sales works. Messages get written in seconds. Lists get prioritized automatically.
            Entire outbound sequences are generated, optimized, and sent — often without a human lifting a finger.
          </Typography>
          <Typography paragraph>Some say sales teams will disappear altogether.</Typography>
          <Typography paragraph>
            Elon Musk recently said, "Probably none of us will have a job… If you want to do a job that's kinda like a
            hobby, you can do a job. But otherwise, AI and the robots will provide any goods and services that you
            want."
          </Typography>
          <Typography paragraph>
            Bill Gates went further, predicting that AI will soon take over most roles in sectors like education,
            logistics, and healthcare — calling them "basically solved problems."
          </Typography>
          <Typography paragraph>
            We agree: AI is going to take over a lot. But here's the thing it doesn't have — and never will: human
            credibility.
          </Typography>
          <Typography paragraph>
            You can automate touchpoints. You can optimize timing. But trust? You have to earn that. And it doesn't come
            from a sequence — it comes from a name.
          </Typography>
          <Typography paragraph>
            When someone your buyer trusts makes the intro, the whole conversation changes. They actually listen. They
            stay longer. They move faster.
          </Typography>
          <Typography paragraph>That's why referrals aren't going away — they're becoming more important.</Typography>
          <Typography paragraph>
            <strong>The smartest GTM teams are combining both</strong>
            <br />
            The best go-to-market teams aren't ignoring AI — they're using it to streamline everything except trust.
            They let AI do the heavy lifting. But when it's time to open real doors, they turn to someone their buyers
            already know.
          </Typography>
          <Typography paragraph>
            <strong>One thing AI can't do: vouch for you</strong>
            <br />
            It can write like you. Sound like you. Guess what to say next. But it doesn't have a reputation. It doesn't
            have relationships. It can't say: "I know this team — they're worth your time."
          </Typography>
          <Typography paragraph>
            That's what intros do. And that's why they'll matter more — not less — in the years ahead.
          </Typography>
          <Typography paragraph>
            <strong>The opportunity is now</strong>
            <br />
            TechXpander isn't competing with AI — it's built for this new reality. We help companies reach the right
            people faster, by working with professionals who already have their trust.
          </Typography>
          <Typography paragraph>
            AI can help you scale.
            <br />
            Warm intros help you land.
            <br />
            That's not a workaround.
            <br />
            That's the edge.
          </Typography>
        </Box>

        {/* Article 2 - Launch */}
        <Box
          id='launch-article'
          sx={{
            backgroundColor: '#ffffff',
            p: { xs: 3, md: 4 },
            borderRadius: 2,
            boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
            mb: 5,
          }}
        >
          <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic', mb: 1 }}>
            8 April 2025
          </Typography>
          <Typography variant='h5' fontWeight='bold' gutterBottom>
            TechXpander Launches Platform
          </Typography>

          <Typography paragraph>
            TechXpander is now live. The platform is designed to connect B2B tech companies with experienced industry
            professionals who can make warm introductions to decision-makers in key markets.
          </Typography>
          <Typography paragraph>
            The goal: solve one of the biggest problems in tech growth — getting in front of the right people quickly,
            without relying on cold outreach, expensive hires, or spray-and-pray marketing.
          </Typography>
          <Typography paragraph>
            TechXpander enables this by standardizing how referrals work in B2B sales. Companies set clear commission
            structures. Lead agents — typically senior professionals with strong networks — choose if and when to
            introduce someone. The platform handles tracking, contracts, and payouts.
          </Typography>
          <Typography paragraph>No vague promises. No scattered email threads. No friction.</Typography>
          <Typography paragraph>
            This system grew out of real-world experience. We've spent years launching tech products across Europe and
            North America. We've seen first-hand how long it takes to build trust in new markets — and how often great
            products stall because the right people never see them.
          </Typography>
          <Typography paragraph>
            TechXpander gives companies a smarter way to open new doors — and gives professionals a new way to activate
            the trust they've spent years building.
          </Typography>
          <Typography paragraph>
            The platform is live and running. More features and tools will roll out over the coming months based on user
            feedback.
          </Typography>
        </Box>
      </Box>
    ),
  },

  faq: {
    title: 'FAQ',
    body: (
      <>
        <Box mb={3} p={3} sx={{ backgroundColor: '#f0f4ff', borderRadius: 2 }}>
          <Typography variant='body1' mb={1}>
            Here's where we answer the most common questions — both for Lead Agents and Tech Companies.
          </Typography>
          <Typography variant='body1'>
            Jump to:{' '}
            <a href='#tech-companies-faq' style={{ color: '#1976d2', textDecoration: 'underline' }}>
              Tech Companies FAQ
            </a>
          </Typography>
        </Box>

        {/* LEAD AGENTS SECTION */}
        <Box p={3} sx={{ backgroundColor: '#ffffff', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
          <Typography variant='h5' fontWeight='bold' gutterBottom>
            Lead Agents — FAQ
          </Typography>

          {[
            [
              'What does TechXpander do?',
              'We connect industry professionals like you with tech companies who pay attractive commissions when you introduce them to buyers you have access to.',
            ],
            ['What is a Lead Agent?', 'You - the industry professional.'],

            ['Is this a full-time job?', 'No. You decide when and how often to get involved.'],
            [
              'So how should I think about this?',
              'TechXpander offers industry professionals like you a low-effort way to turn your professional network into meaningful income — without needing to sell.',
            ],

            ["What do you mean there's no need to sell?", 'You intro and get paid. The client takes it from there.'],
            ['How much time does this take?', 'Most agents spend 2.5 to 4 hours to get their first meeting.'],
            ['What after that?', 'About 1–2 hours per meeting.'],
            ['Can I work with more than one client?', 'Yes, if your contacts match their needs.'],
            ['How do I start?', 'We begin with a 30-minute intro call with the client.'],
            [
              'What comes next?',
              'If both sides agree, you sign an agreement with TechXpander and get access to the platform.',
            ],
            [
              "What's in the platform?",
              'A comprehensive overview of clients, their commission structures, and product documentation.',
            ],
            ['Can I make intros right away?', 'No. You need client approval for each contact you want to introduce.'],
            [
              'How do I get approval?',
              'You use a shared sheet with other agents. The client approves contacts before you reach out.',
            ],
            ['How do intros work?', 'Once approved, you schedule a call and do the intro live.'],
            [
              'Do I stay for the full call?',
              "It is the smart thing to do, but you don't have to. After your 5-min intro, you're eligible for commission.",
            ],
            [
              'Summarizing, what needs to happen for me to earn?',
              '1. Client approval. 2. Agreement signed. 3. Contact approval. 4. Call set up. 5. Live 5-min intro made.',
            ],
            [
              'Should I stay involved?',
              'You can, yes — it keeps you close to the deal and helps you guide it forward, which leads to faster commission.',
            ],
            [
              'Do I join follow-ups?',
              'Optional and this is all between you and the client — but it often makes a difference in deal momentum.',
            ],
            [
              'Do I need to report updates?',
              'Only when commission is due. Then you log it in the platform for the client to approve.',
            ],
            ['What happens then?', 'At the end of each month, approved commissions get locked for payout.'],
            ['How does payment work?', 'Client pays TechXpander. We pay you.'],
            [
              'When do I get paid?',
              'At the start of each month, TechXpander invoices the client for commissions approved during the previous month.',
            ],
            ['And then?', 'Within 7 days after the client pays, you will get paid.'],
            [
              "Will I get paid if the client doesn't pay?",
              'No — payouts only happen after the client has paid TechXpander.',
            ],
            ['How much can I earn per meeting?', '$300–$1000 depending on the client.'],
            ['Can I earn more?', 'Yes. Some clients offer % of monthly revenue or bonus payouts.'],
            [
              "Where's an example?",
              <>
                <span style={{ fontSize: '0.875rem' }}>
                  <Link to='/content/commissionExamples' style={{ textDecoration: 'underline', color: '#1976d2' }}>
                    Click here
                  </Link>{' '}
                  to see an example.
                </span>
              </>,
            ],

            [
              'How does this work legally?',
              'You sign one agreement with TechXpander. We handle contracts with the clients.',
            ],
            ['What kind of clients use TechXpander?', 'Emerging B2B tech companies with a proven product-market fit.'],
            ["What's expected of me?", 'Nothing formal. But more effort means more earnings.'],
            ['Can I work with the client outside of TechXpander?', "No. Always ask us if you're unsure."],
          ].map(([q, a], index) => (
            <Box key={index} mb={3}>
              <Typography variant='subtitle1' fontWeight='bold'>
                Q: {q}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                A: {a}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* DIVIDER */}
        <Box my={6}>
          <hr style={{ border: 'none', borderTop: '1px solid #ccc' }} />
        </Box>

        {/* TECH COMPANIES SECTION */}
        <Box id='tech-companies-faq' p={3} sx={{ backgroundColor: '#fdfdfd', borderRadius: 2 }}>
          <Typography variant='h5' fontWeight='bold' gutterBottom>
            Tech Companies — FAQ
          </Typography>

          {[
            [
              'What does TechXpander do?',
              'We help tech companies connect with industry professionals (Lead Agents) who can intro you to your buyers.',
            ],
            [
              'Is this performance marketing?',
              "No — it's performance-based networking or b2b referrals: real intros, real people, not clicks.",
            ],
            [
              'Who are your Lead Agents?',
              'Experienced industry professionals with real networks and trusted access to decision-makers.',
            ],
            [
              'How do you find them?',
              'We recruit and vet agents with proven networks in your target regions and industries.',
            ],
            [
              'Do I control the agents?',
              'No. They work independently — we help you make it worth their time, they decide to act.',
            ],
            ['What markets do you cover?', 'The US, Canada, Western Europe, the UK, and Turkey.'],
            [
              'How fast can we get started?',
              'Once you register, sign the contract, and onboard — we can start within days.',
            ],
            [
              'Can I bring my own agents?',
              'Yes. You can invite them to join the platform. Lower fees apply and you earn from what they generate for others.',
            ],
            ['How do I do that?', "Just introduce them to us — we'll onboard and support them."],
            ['Do I need to commit long term?', 'No. You can pause or stop at any time.'],
            ['What do I pay?', 'You pay a commission to the agent, plus a TechXpander fee.'],
            ['Anything else?', 'No. Platform use is free during early access.'],
            ['So what if nothing happens?', 'Then you pay nothing.'],
            [
              'What does TechXpander handle?',
              'We manage onboarding, tracking, contracts, approvals, invoicing, and payouts.',
            ],
            [
              'Do I need to train the agents?',
              'Just enough so they can book meetings for you — they intro, you pitch.',
            ],
            [
              'How are commissions structured?',
              'You decide: per meeting, per milestone, or deal. We help make it competitive and clear.',
            ],
            ['Do I need to pay anything upfront?', 'No. You only pay for results: meetings or customers.'],
            [
              'Can I offer extra incentives?',
              'Yes. You can add things like bonuses or retainers for individual agents.',
            ],
            ['Why would I do that?', 'It builds commitment — agents are more likely to focus on you.'],
            ['Can I scale across regions?', "Yes. That's exactly what the platform is designed for."],
            [
              'How are results tracked?',
              'All meetings and other commission events are logged and visible in your dashboard.',
            ],
            ['How does it work financially?', 'You pay TechXpander. We pay the Lead Agents.'],
            ['How does commission flow work?', 'Agent logs a commission request — you approve it.'],
            ['What happens after approval?', 'Approved commissions are locked at month-end.'],
            ['And then?', 'You get invoiced. Once you pay, the agents get paid 7 days later.'],
            ['How does it work legally?', 'You sign one agreement with us. We handle all Lead Agent contracts.'],
            [
              'Can agents act without my approval?',
              'No. You must approve both the agent and the contact of the agent.',
            ],
            ['How do I communicate with Lead Agents?', 'TechXpander sets up Slack for all involved.'],
            [
              'Summarizing, when is an agent eligible for commission?',
              '1. Agent approved 2. Contact approved 3. Meeting happens 4. Intro made 5. Logged 6. Approved.',
            ],
            [
              'Can I work with agents outside the platform?',
              'No — all interactions must go through TechXpander. If unsure, just ask us.',
            ],
          ].map(([q, a], index) => (
            <Box key={index} mb={3}>
              <Typography variant='subtitle1' fontWeight='bold'>
                Q: {q}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                A: {a}
              </Typography>
            </Box>
          ))}
        </Box>
      </>
    ),
  },

  'privacy-policy': {
    title: 'Privacy Policy',
    body: (
      <Box>
        {/* Light grey top section with intro and date */}
        <Box
          sx={{
            backgroundColor: '#f4f6fa',
            p: 3,
            borderRadius: 2,
            mb: 4,
          }}
        >
          <Typography variant='body2'>
            We take privacy seriously — for both clients and lead agents. Here's how we protect your data and what you
            can expect from us.
          </Typography>
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
            Last updated: 8 April 2025
          </Typography>
        </Box>

        {/* Main policy content */}
        <Box sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
          <Typography variant='subtitle2' fontWeight='bold' gutterBottom sx={{ mt: 3 }}>
            Passwords
          </Typography>
          <Typography variant='body2' paragraph>
            Passwords are stored using a hashed format. No one at TechXpander can see or retrieve them. We never store
            your real password.
          </Typography>

          <Typography variant='subtitle2' fontWeight='bold' gutterBottom sx={{ mt: 3 }}>
            For Clients
          </Typography>
          <Typography variant='body2' paragraph>
            If you share product details, commission plans, or other company info, that data is only visible to lead
            agents who are logged into the platform — and only if they've been granted access. No one else can see it.
          </Typography>

          <Typography variant='subtitle2' fontWeight='bold' gutterBottom sx={{ mt: 3 }}>
            For Lead Agents
          </Typography>
          <Typography variant='body2' paragraph>
            Your presence on the platform is never shown to clients. Only TechXpander staff can see who's active.
            Clients cannot browse or find agents on their own.
          </Typography>

          <Typography variant='subtitle2' fontWeight='bold' gutterBottom sx={{ mt: 3 }}>
            Data Storage & Access
          </Typography>
          <Typography variant='body2' paragraph>
            All information you share is encrypted and stored securely. We do not sell or share your data with anyone.
            Access is restricted to verified TechXpander staff only, for operational use.
          </Typography>

          <Typography variant='subtitle2' fontWeight='bold' gutterBottom sx={{ mt: 3 }}>
            Bank Details
          </Typography>
          <Typography variant='body2' paragraph>
            If you provide your bank account information for payment purposes, it is handled securely and only used to
            process payouts. This data is never shared with third parties.
          </Typography>

          <Typography variant='subtitle2' fontWeight='bold' gutterBottom sx={{ mt: 3 }}>
            Data Retention
          </Typography>
          <Typography variant='body2' paragraph>
            We keep your account data as long as your account is active. You can request deletion at any time by
            emailing:{' '}
            <a href='mailto:support@techxpander.com' style={{ color: '#1976d2' }}>
              support@techxpander.com
            </a>
          </Typography>

          <Typography variant='subtitle2' fontWeight='bold' gutterBottom sx={{ mt: 3 }}>
            Cookies & Analytics
          </Typography>
          <Typography variant='body2' paragraph>
            We do not use cookies, analytics tools, or tracking scripts. Your behavior on the platform is not monitored
            or shared.
          </Typography>
          <Typography variant='body2' paragraph>
            We may update this Privacy Policy from time to time. All changes will be posted on this page with an updated
            revision date.
          </Typography>
          <Typography variant='body2' paragraph>
            Still have questions? Email us at{' '}
            <a href='mailto:support@techxpander.com' style={{ color: '#1976d2' }}>
              support@techxpander.com
            </a>
            .
          </Typography>
        </Box>
      </Box>
    ),
  },

  terms: {
    title: 'Terms & Conditions For Lead Agents',
    body: (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 6 },
          maxWidth: 900,
          width: '100%',
          backgroundColor: '#fff',
          borderLeft: '6px solid #1976d2',
          mx: 'auto',
        }}
      >
        <Typography variant='body2' fontStyle='italic' color='text.secondary' gutterBottom sx={{ mb: 4 }}>
          These TCs apply to all Lead Agents who have a signed agreement in place with TechXpander. Last updated:
          15-04-2025 — V1.0
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          1. When Commission Is Payable
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • Commission is only paid if:
          <br />• i. The Lead Agent introduces a Prospect successfully
          <br />• ii. The Prospect was approved by the Client
          <br />• iii. The Client pays the Facilitator
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          2. Status
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • Lead Agents work independently.
          <br />• They are not employees.
          <br />• They act on their own behalf.
          <br />• They must act professionally at all times.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          3. Legal and Tax
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • Lead Agents follow their own local laws.
          <br />• They handle their own taxes.
          <br />• The Facilitator is not responsible for legal or tax issues.
          <br />• Any false claims or misrepresentation by the Lead Agent is their own responsibility.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          4. Commission Process
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • Commission requests should be submitted in the Platform.
          <br />• Clients can approve or decline.
          <br />• At 12:00 AM on the first day of the new month, only approved requests are locked for pay out.
          <br />• Invoices can be downloaded from the Platform.
          <br />• Lead Agents may assign invoice numbers but cannot send their own invoices.
          <br />• If you invoice with a Dutch business registration, VAT (BTW) will be added.
          <br />• If you invoice as a natural person or from outside the Netherlands, no VAT is added.
          <br />• Payment is made within 7 business days after the Client has paid the Facilitator.
          <br />• If the Client does not pay, pays only part, or goes bankrupt, Commission may be reduced or not paid at
          all.
          <br />• Mistakes or missing info in the Commission request are at the Lead Agent's own risk.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          5. Buyouts
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • Clients may offer Lead Agents to buy out future Commission rights.
          <br />• Terms are listed in the Client profile accessible for the Lead Agent in the Platform.
          <br />• Once paid, buyouts are final.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          6. Changes to Commission
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • Clients may change Commission and buyout terms at any time.
          <br />• These changes only apply to new Prospects.
          <br />• The Commission structure at the time of the first call between Client and Prospect (set up by the Lead
          Agent) applies.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          7. Disputes
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • If there's a Commission dispute between Lead Agent and Client, the Commission is halved.
          <br />• This affects both the Facilitator and Lead Agent.
          <br />• If the dispute continues, the Facilitator's decision is final.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          8. Representation Rules
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • A Lead Agent can only act for a Client after written (email) approval.
          <br />• If approval is revoked, the Lead Agent still gets paid for rights already in effect.
          <br />• No Commission will be paid for new actions taken after revocation.
          <br />• Working with Clients directly, bypassing the Facilitator, is not allowed during the contract and for 3
          years after.
          <br />• All direct offers from Clients must be reported.
          <br />• Breaking these rules may lead to termination in bad faith, loss of unpaid Commission, and removal from
          the Platform.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          9. Confidentiality
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • Lead Agents must not share any documents, agreements, or Platform data.
          <br />• Any potential NDAs with Clients must be handled by the Lead Agent.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          10. Liability
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • Lead Agents are responsible for any harm or legal problems they cause.
          <br />• They must cover any damage claims made against the Facilitator due to their actions.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          11. Updates
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • The Facilitator can update these terms at any time.
          <br />• The current version is always online:{' '}
          <Link to='/content/terms' style={{ color: '#1976d2' }}>
            https://techxpander.com/terms/lead-agents
          </Link>
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          12. Privacy
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • The Facilitator only uses personal data to run the Platform and handle payments.
          <br />• Privacy policy:{' '}
          <Link to='/content/privacy-policy' style={{ color: '#1976d2' }}>
            https://techxpander.com/privacy
          </Link>
          <br />• To delete your account:{' '}
          <Link to='/content/user-data-deletion' style={{ color: '#1976d2' }}>
            https://techxpander.com/delete-my-data
          </Link>{' '}
          and send an email to the listed address.
        </Typography>
      </Paper>
    ),
  },
  'client-terms': {
    title: 'Terms & Conditions For Clients',
    body: (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 6 },
          maxWidth: 900,
          width: '100%',
          backgroundColor: '#fff',
          borderLeft: '6px solid #1976d2',
          mx: 'auto',
        }}
      >
        <Typography variant='body2' fontStyle='italic' color='text.secondary' gutterBottom sx={{ mb: 4 }}>
          These TCs apply to all Clients who have a signed agreement in place with TechXpander. Last updated: 15-04-2025
          — V2.0
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          1. Commission Workflow
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • Lead Agents submit Commission requests during the month in the Platform.
          <br />• All submitted Commissions by Lead Agents in the Platform must be approved or declined by the Client
          within 3 business days.
          <br />• Approved Commissions will be automatically locked for payment at month-end 12:00 AM on the first day
          of the new month.
          <br />• Invoices are generated by the platform based on these locked Commissions.
          <br />• The Facilitator will pay Lead Agents only after receiving funds from the Client.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          2. Payment Terms
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • The Client may change Commission structures at any time, but only in coordination with the Facilitator.
          <br />• Commission is only payable if the related event occurred and the Lead Agent and the Prospect were
          approved by the Client.
          <br />• For revenue-based Commissions, payment is due after the Client receives payment from the Customer or
          within 60 days after invoice date, whichever happens first.
          <br />• For one-off Commissions, payment from the Client is due within 7 business days of receiving the
          Facilitator's invoice.
          <br />• This short payment term is to make sure that Lead Agents get paid as quickly as possible.
          <br />• If the Customer does not pay, pays partially, or disputes a charge, revenue-based Commission payouts
          may be reduced, postponed or cancelled.
          <br />• In case of payment issues, the Client must inform the Facilitator and Lead Agent as soon as possible
          and show proof.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          3. Buyouts
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • The Client may offer to buy out the Facilitator (and the Lead Agent) from future Commissions.
          <br />• Buyout terms, if applicable, should be defined in the Platform.
          <br />• Buyouts, once paid, are final and non-negotiable.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          4. Commission Disputes
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • If a dispute arises between the Client and Lead Agent about a Commission, the amount will be halved.
          <br />• If both Lead Agent and Client do not agree with this resolution, the Facilitator will decide the
          outcome, which is binding for all parties.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          5. Approval and Termination of Lead Agents
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • A Lead Agent may only contact Prospects once both the Lead Agent and the Prospect have been approved by the
          Client.
          <br />• Lead Agent approval may be revoked at any time by email to both the Facilitator and Lead Agent.
          <br />• If revoked in good faith, Commissions remain payable for results from efforts that occurred prior to
          revocation.
          <br />• If an introduction leads to a deal within 12 months after the termination date, Commission remains due
          as long as the introduction happened before revocation and the termination was done in good faith.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          6. Communication
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • The Facilitator is responsible to create and manage Slack groups, WhatsApp groups and/or online target lists
          for collaboration.
          <br />• The Client is responsible for sharing updates, feedback, and needed materials with Lead Agents and in
          the Platform.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          7. Confidentiality
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • The Client must not share the contents of this Agreement, the Platform, or the way of working of the
          Facilitator.
          <br />• This obligation continues after the Agreement ends.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          8. Indemnification
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • The Client indemnifies the Facilitator against claims or damages caused by its own actions or its Lead
          Agents.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          9. Updates
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • These TCs may be updated by the Facilitator at any time.
          <br />• The latest version is always available at:{' '}
          <Link to='/content/client-terms' style={{ color: '#1976d2' }}>
            https://techxpander.com/terms/clients
          </Link>
        </Typography>
        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          10. Privacy
        </Typography>
        <Typography variant='body2' paragraph sx={{ textTransform: 'uppercase', fontSize: '0.60rem' }}>
          • The Facilitator only uses personal data to run the Platform and handle payments.
          <br />• Privacy policy:{' '}
          <Link to='/content/privacy-policy' style={{ color: '#1976d2' }}>
            https://techxpander.com/privacy
          </Link>
          <br />• To delete your account:{' '}
          <Link to='/content/user-data-deletion' style={{ color: '#1976d2' }}>
            https://techxpander.com/delete-my-data
          </Link>{' '}
          and send an email to the listed address.
        </Typography>
      </Paper>
    ),
  },
  'user-data-deletion': {
    title: 'User Data Deletion',
    body: (
      <Box>
        <Typography variant='h6' fontWeight='bold' gutterBottom>
          Request User Data Deletion
        </Typography>
        <Typography paragraph>
          If you'd like to delete your account and personal data from our platform, please reach out to us directly at:
        </Typography>
        <Typography variant='body2'>
          📧 <strong>Email:</strong> <a href='mailto:support@techxpander.com'>support@techxpander.com</a>
        </Typography>
        <Typography paragraph>We're here to help! Please don't hesitate to contact us with any questions.</Typography>
      </Box>
    ),
  },
  'terms-of-service': {
    title: 'Terms of Service',
    body: (
      <Box sx={{ fontSize: '0.85rem', lineHeight: 1.7 }}>
        <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic', mb: 2 }}>
          Last updated: April 17, 2025
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          1. Who can use this platform
        </Typography>
        <Typography paragraph>
          To use TechXpander, you must:
          <br />
          - Be at least 18 years old
          <br />
          - Use the platform for business purposes only
          <br />- Not break any laws while using it
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          2. Accounts and access
        </Typography>
        <Typography paragraph>
          Some features require an account. You're responsible for your account info. Don't share login details or
          pretend to be someone else.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          3. Use of content
        </Typography>
        <Typography paragraph>
          The platform, logo, branding, and everything on this site belong to us. Don't copy, resell, or reuse anything
          without our written permission.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          4. Client and Lead Agent terms
        </Typography>
        <Typography paragraph sx={{ mb: 2 }}>
          If you become a Client or a Lead Agent on the platform, you'll need to sign an agreement, which includes the
          specific Terms & Conditions for your role:
          <br />•{' '}
          <Link
            to='/content/client-terms'
            style={{
              color: '#1976d2',
              textDecoration: 'underline',
            }}
          >
            Client Terms & Conditions
          </Link>
          <br />•{' '}
          <Link
            to='/content/terms'
            style={{
              color: '#1976d2',
              textDecoration: 'underline',
            }}
          >
            Lead Agent Terms & Conditions
          </Link>
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          5. Privacy
        </Typography>
        <Typography paragraph>
          We care about your privacy. Read our{' '}
          <Link to='/content/privacy-policy' style={{ color: '#1976d2', textDecoration: 'underline' }}>
            Privacy Policy
          </Link>{' '}
          to learn how we handle your data.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          6. No guarantees
        </Typography>
        <Typography paragraph>
          We do our best to keep the platform running smoothly, but we can't guarantee uptime or results. We're not
          responsible for any issues caused by using this platform.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          7. Changes
        </Typography>
        <Typography paragraph>
          We can update these terms at any time. If you keep using the platform after we change something, it means you
          accept the new version.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          8. Contact
        </Typography>
        <Typography paragraph>
          Questions? Email us at{' '}
          <Link to='mailto:support@techxpander.com' style={{ color: '#1976d2', textDecoration: 'underline' }}>
            support@techxpander.com
          </Link>
          .
        </Typography>
      </Box>
    ),
  },
};

function WhatsNewDynamicContent() {
  const { data: articles = [], isLoading } = useQuery(getPublishedArticles({}));

  const formatDate = (d) => formatDisplayDate(d, '');

  if (isLoading) {
    return (
      <Box display='flex' justifyContent='center' py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          backgroundColor: '#f4f6fa',
          p: 3,
          borderRadius: 2,
          mb: 4,
        }}
      >
        <Typography variant='h6' fontWeight='bold' gutterBottom>
          Recent Updates
        </Typography>
        {articles.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            No updates yet.
          </Typography>
        ) : (
          <ul style={{ listStyle: 'none', paddingLeft: 0, marginBottom: 0 }}>
            {articles.map((article) => (
              <li key={article._id} style={{ marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Typography variant='body2' component='span' sx={{ color: '#666', fontSize: '0.8rem', flexShrink: 0 }}>
                  {formatDate(article.date)}
                </Typography>
                <a
                  href={`#article-${article._id}`}
                  style={{
                    textDecoration: 'none',
                    color: '#1976d2',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  {article.title}
                </a>
              </li>
            ))}
          </ul>
        )}
      </Box>

      {articles.map((article) => (
        <Box
          id={`article-${article._id}`}
          key={article._id}
          sx={{
            backgroundColor: '#ffffff',
            borderLeft: '6px solid #1976d2',
            p: { xs: 3, md: 4 },
            borderRadius: 2,
            boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
            mb: 5,
          }}
        >
          <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic', mb: 1 }}>
            {formatDate(article.date)}
          </Typography>
          <Typography
            variant='h5'
            fontWeight='bold'
            gutterBottom
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {article.title}
          </Typography>
          <Typography component='div' sx={{ whiteSpace: 'pre-wrap' }}>
            {article.content || ''}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function TermsDynamicContent({ type }) {
  const { data, isLoading } = useQuery(getTermsByType(type));

  if (isLoading) {
    return (
      <Box display='flex' justifyContent='center' py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const content = data?.content ?? '';

  return (
    <Paper
      elevation={0}
      sx={{
        borderLeft: '6px solid #1976d2',
        borderRadius: 2,
        p: { xs: 2, sm: 3, md: 5 },
        backgroundColor: '#fff',
        boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
      }}
    >
      <Box
        sx={{
          '& p, & li': {
            fontSize: { xs: '0.875rem', sm: '1rem' },
            lineHeight: { xs: 1.6, sm: 1.7 },
          },
          '& h6': {
            fontSize: { xs: '1rem', sm: '1.1rem' },
            mt: { xs: 2, sm: 3 },
          },
        }}
      >
        <Typography component='div' sx={{ whiteSpace: 'pre-wrap' }}>
          {content || 'No content set yet.'}
        </Typography>
      </Box>
    </Paper>
  );
}

const GenericContentPage = () => {
  const { pageType } = useParams();
  const theme = useTheme();

  // Scroll to top when pageType changes
  useEffect(() => {
    document.getElementById('content-container')?.scrollTo(0, 0);
  }, [pageType]);

  const pageData = contentMap[pageType] || {
    title: 'Page Not Found',
    body: <Typography>The page you're looking for doesn't exist or hasn't been created yet.</Typography>,
  };

  return (
    <Box
      sx={{
        position: 'relative',
        backgroundColor: '#f9fbfd',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          left: -150,
          width: 300,
          height: 300,
          background: 'radial-gradient(circle at top left, #e3f2fd, transparent)',
          zIndex: 0,
          [theme.breakpoints.down('sm')]: {
            width: 200,
            height: 200,
            top: -50,
            left: -100,
          },
        }}
      />
      <Container
        maxWidth='md'
        sx={{
          position: 'relative',
          zIndex: 1,
          py: { xs: 4, sm: 6, md: 8 },
          flex: 1,
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Typography
          variant='h4'
          fontWeight='bold'
          color='primary'
          gutterBottom
          sx={{
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
            mb: { xs: 2, sm: 3 },
          }}
        >
          {pageData.title}
        </Typography>

        {pageType === 'whats-new' ? (
          <WhatsNewDynamicContent />
        ) : pageType === 'terms' ? (
          <TermsDynamicContent type='lead' />
        ) : pageType === 'client-terms' ? (
          <TermsDynamicContent type='client' />
        ) : (
          <Paper
            elevation={0}
            sx={{
              borderLeft: '6px solid #1976d2',
              borderRadius: 2,
              p: { xs: 2, sm: 3, md: 5 },
              backgroundColor: '#fff',
              boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
            }}
          >
            <Box
              sx={{
                '& p, & li': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  lineHeight: { xs: 1.6, sm: 1.7 },
                },
                '& h6': {
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  mt: { xs: 2, sm: 3 },
                },
              }}
            >
              {pageData.body}
            </Box>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default GenericContentPage;
